import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, MapPin, Phone, Mail, Pencil, Trash2, Building2 } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/school")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "School profile — MAKAI" }] }),
  component: SchoolPage,
});

type SchoolRow = {
  id: string; name: string; motto: string | null; logo_url: string | null;
  email: string | null; phone: string | null; website: string | null;
  country: string; district: string | null; address: string | null;
  founded_year: number | null; currency: string; timezone: string;
};
type Campus = { id: string; school_id: string; name: string; code: string | null; address: string | null; phone: string | null; email: string | null; is_main: boolean };

function SchoolPage() {
  const { active } = useSchoolContext();
  const [school, setSchool] = useState<SchoolRow | null>(null);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!active) return;
    const [s, c] = await Promise.all([
      supabase.from("schools").select("*").eq("id", active.school_id).maybeSingle(),
      supabase.from("campuses").select("*").eq("school_id", active.school_id).order("is_main", { ascending: false }).order("name"),
    ]);
    if (s.data) setSchool(s.data as SchoolRow);
    setCampuses((c.data ?? []) as Campus[]);
  }, [active]);

  useEffect(() => { load(); }, [load]);

  async function saveSchool(e: React.FormEvent) {
    e.preventDefault();
    if (!school) return;
    setSaving(true);
    const { error } = await supabase.from("schools").update({
      name: school.name, motto: school.motto, email: school.email, phone: school.phone,
      website: school.website, district: school.district, address: school.address,
      founded_year: school.founded_year, currency: school.currency,
    }).eq("id", school.id);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    toast.success("School profile updated");
  }

  if (!school) return <AdminShell title="School"><div className="grid h-48 place-items-center text-muted-foreground">Loading…</div></AdminShell>;

  return (
    <AdminShell title="School profile" subtitle="Public details that appear on receipts, ID cards and parent-facing pages.">
      <form onSubmit={saveSchool} className="grid gap-6 rounded-2xl border border-border bg-card p-6 shadow-soft md:grid-cols-2">
        <Field label="School name *" className="md:col-span-2">
          <Input value={school.name} onChange={(e) => setSchool({ ...school, name: e.target.value })} required />
        </Field>
        <Field label="Motto" className="md:col-span-2">
          <Input value={school.motto ?? ""} onChange={(e) => setSchool({ ...school, motto: e.target.value })} />
        </Field>
        <Field label="District"><Input value={school.district ?? ""} onChange={(e) => setSchool({ ...school, district: e.target.value })} /></Field>
        <Field label="Founded year"><Input type="number" value={school.founded_year ?? ""} onChange={(e) => setSchool({ ...school, founded_year: e.target.value ? Number(e.target.value) : null })} /></Field>
        <Field label="Phone"><Input value={school.phone ?? ""} onChange={(e) => setSchool({ ...school, phone: e.target.value })} /></Field>
        <Field label="Email"><Input type="email" value={school.email ?? ""} onChange={(e) => setSchool({ ...school, email: e.target.value })} /></Field>
        <Field label="Website" className="md:col-span-2"><Input value={school.website ?? ""} onChange={(e) => setSchool({ ...school, website: e.target.value })} placeholder="https://" /></Field>
        <Field label="Currency"><Input value={school.currency} onChange={(e) => setSchool({ ...school, currency: e.target.value.toUpperCase() })} maxLength={3} /></Field>
        <Field label="Timezone"><Input value={school.timezone} disabled /></Field>
        <Field label="Address" className="md:col-span-2"><Textarea value={school.address ?? ""} onChange={(e) => setSchool({ ...school, address: e.target.value })} rows={2} /></Field>
        <div className="md:col-span-2">
          <Button type="submit" disabled={saving} className="bg-primary hover:bg-primary/90">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
          </Button>
        </div>
      </form>

      <section className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold">Campuses</h2>
          <CampusDialog schoolId={school.id} onSaved={load} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {campuses.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-start justify-between">
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary-soft text-primary"><Building2 className="h-5 w-5" /></div>
                {c.is_main && <span className="rounded-md bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase text-gold-foreground">Main</span>}
              </div>
              <h3 className="mt-3 font-display text-base font-bold">{c.name}</h3>
              {c.address && <p className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground"><MapPin className="mt-0.5 h-3 w-3 shrink-0" /> {c.address}</p>}
              {c.phone && <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Phone className="h-3 w-3" /> {c.phone}</p>}
              {c.email && <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground"><Mail className="h-3 w-3" /> {c.email}</p>}
              <div className="mt-3 flex gap-1.5">
                <CampusDialog schoolId={school.id} campus={c} onSaved={load} trigger={<Button size="sm" variant="outline"><Pencil className="h-3.5 w-3.5" /></Button>} />
                {!c.is_main && (
                  <Button size="sm" variant="outline" onClick={async () => {
                    if (!confirm("Delete this campus?")) return;
                    const { error } = await supabase.from("campuses").delete().eq("id", c.id);
                    if (error) toast.error(error.message); else { toast.success("Campus removed"); load(); }
                  }}><Trash2 className="h-3.5 w-3.5" /></Button>
                )}
              </div>
            </div>
          ))}
          {campuses.length === 0 && <div className="col-span-full rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No campuses yet.</div>}
        </div>
      </section>
    </AdminShell>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={"space-y-1.5 " + (className ?? "")}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function CampusDialog({ schoolId, campus, onSaved, trigger }: { schoolId: string; campus?: Campus; onSaved: () => void; trigger?: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<Partial<Campus>>(campus ?? { name: "", code: "", address: "", phone: "", email: "", is_main: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && campus) setForm(campus); }, [open, campus]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    setBusy(true);
    const payload = { school_id: schoolId, name: form.name!, code: form.code || null, address: form.address || null, phone: form.phone || null, email: form.email || null, is_main: !!form.is_main };
    const { error } = campus
      ? await supabase.from("campuses").update(payload).eq("id", campus.id)
      : await supabase.from("campuses").insert(payload);
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success(campus ? "Campus updated" : "Campus added");
    setOpen(false); onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button size="sm" className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Add campus</Button>}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{campus ? "Edit campus" : "New campus"}</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Name *"><Input value={form.name ?? ""} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
          <Field label="Code"><Input value={form.code ?? ""} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MAIN" /></Field>
          <Field label="Address"><Textarea value={form.address ?? ""} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone"><Input value={form.phone ?? ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email"><Input type="email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <Label className="text-sm">Main campus</Label>
            <Switch checked={!!form.is_main} onCheckedChange={(v) => setForm({ ...form, is_main: v })} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
